import {
	forwardRef,
	useEffect,
	useImperativeHandle,
	useRef,
	useState,
} from "react";
import { PlusOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import { Button, Input, Tag, Tooltip } from "antd";

interface TagInputProps {
	tags: string[];
	setTags: (tags: string[]) => void;
	primaryColor?: string;
	maxTags?: number;
	placeholder?: string;
	label?: string;
	tooltipTitle?: string;
}

export interface TagInputRef {
	confirmInput: () => void;
	getCurrentInput: () => string;
}

export const TagInput = forwardRef<TagInputRef, TagInputProps>(
	(
		{
			tags = [],
			setTags,
			primaryColor = "#99CA29",
			maxTags = 10,
			placeholder = "LINE WORKSに設定されている自分のトークIDを入力",
			label = "管理者追加",
			tooltipTitle,
		},
		ref,
	) => {
		const [inputVisible, setInputVisible] = useState(false);
		const [inputValue, setInputValue] = useState("");
		const [inputError, setInputError] = useState<string | undefined>(undefined);
		const inputRef = useRef<HTMLInputElement>(null);

		const handleClose = (removedTag: string) => {
			const newTags = tags?.filter((tag) => tag !== removedTag);
			setTags(newTags);
		};

		const showInput = () => {
			setInputVisible(true);
			setInputError(undefined);
		};

		const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
			setInputValue(e.target.value);
			setInputError(undefined);
		};

		const handleInputConfirm = () => {
			const newTag = inputValue.trim();
			if (newTag) {
				if (tags?.includes(newTag)) {
					setInputError("このトークIDは既に登録されています");
					return;
				}
				if (tags && tags.length >= maxTags) {
					setInputError(`トークIDの登録は上限${maxTags}個です`);
					return;
				}
				setTags([...(Array.isArray(tags) ? tags : []), newTag]);
			}
			setInputVisible(false);
			setInputValue("");
			setInputError(undefined);
		};

		const handleRemoveTag = (e: React.MouseEvent, tag: string) => {
			e.preventDefault();
			handleClose(tag);
			if (tag === inputValue.trim()) {
				setInputError(undefined);
			}
		};

		useImperativeHandle(ref, () => ({
			confirmInput: handleInputConfirm,
			getCurrentInput: () => inputValue.trim(),
		}));

		useEffect(() => {
			if (inputVisible) {
				inputRef.current?.focus();
			}
		}, [inputVisible]);

		const defaultTooltipTitle = `※ LINE WORKSに設定されている自分のトークIDを登録してください。\n※ 自分のトークIDの登録は上限${maxTags}個です。`;

		return (
			<div className="mb-4">
				<div className="font-bold text-black mb-4 flex items-center">
					<span>{label}</span>
					<Tooltip
						title={
							<div className="whitespace-pre-line">
								{tooltipTitle || defaultTooltipTitle}
							</div>
						}
					>
						<QuestionCircleOutlined
							className="ml-2"
							style={{ color: primaryColor }}
						/>
					</Tooltip>
				</div>
				<div
					className={`border border-gray-300 rounded-lg p-4 ${tags?.length >= maxTags ? "pb-2" : ""}`}
				>
					<div
						className={`flex flex-wrap gap-2 ${tags?.length > 0 || inputVisible ? "mb-2" : ""}`}
					>
						{tags?.map((tag) => (
							<Tag
								key={tag}
								closable
								onClose={(e) => handleRemoveTag(e, tag)}
								className="text-sm break-all whitespace-normal"
							>
								{tag}
							</Tag>
						))}
					</div>
					{inputVisible ? (
						<div>
							<Input
								ref={inputRef}
								size="middle"
								className="w-[360px]"
								value={inputValue}
								onChange={handleInputChange}
								onPressEnter={handleInputConfirm}
								status={inputError ? "error" : ""}
								placeholder={placeholder}
							/>
							{inputError && (
								<div className="text-red-500 text-xs mt-1">{inputError}</div>
							)}
						</div>
					) : (
						(tags?.length < maxTags || !tags) && (
							<Button
								className="w-8 h-8 border-dashed flex items-center justify-center"
								onClick={showInput}
							>
								<PlusOutlined />
							</Button>
						)
					)}
				</div>
			</div>
		);
	},
);

TagInput.displayName = "TagInput";
